/**
 * The UI kit.
 *
 * Before this the app had no <Button>, <Input>, <Dialog> or <Select> — just raw
 * `className="btn-primary"` strings, one hand-rolled modal, and `window.confirm()`
 * for destructive actions (which can't be styled, can't be themed, and says
 * "localhost:5173 says…" above the club's own copy).
 *
 * These are thin. They wrap the existing @layer classes rather than replacing
 * them, so nothing already written has to change to benefit.
 */
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';

// --------------------------------------------------------------------- Button

type Variant = 'primary' | 'ghost' | 'danger' | 'quiet';

const VARIANT_CLASS: Record<Variant, string> = {
  primary: 'btn-primary',
  ghost: 'btn-ghost',
  danger: 'btn-danger',
  quiet: 'btn-quiet',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  loading?: boolean;
  /** Renders full width — common in dialogs and mobile layouts. */
  block?: boolean;
}

export function Button({
  variant = 'primary',
  loading = false,
  block = false,
  disabled,
  children,
  className = '',
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      // A loading button must not be clickable twice — that's how you get two
      // charges, two invites, two announcements.
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`${VARIANT_CLASS[variant]} ${block ? 'w-full' : ''} ${className}`}
      {...rest}
    >
      {loading ? (
        <span
          className="mr-2 h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden="true"
        />
      ) : null}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------- Field

function FieldShell({
  label,
  hint,
  error,
  id,
  children,
}: {
  label?: string;
  hint?: string;
  error?: string | null;
  id: string;
  children: ReactNode;
}) {
  return (
    <div>
      {label ? (
        <label htmlFor={id} className="mb-1 block text-sm text-cream-200">
          {label}
        </label>
      ) : null}
      {children}
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1 text-xs text-cream-300/45">{hint}</p>
      ) : null}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string | null;
}

export function Input({ label, hint, error, className = '', ...rest }: InputProps) {
  const auto = useId();
  const id = rest.id ?? auto;
  return (
    <FieldShell label={label} hint={hint} error={error} id={id}>
      <input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`field ${error ? 'border-danger focus:border-danger focus:ring-danger' : ''} ${className}`}
        {...rest}
      />
    </FieldShell>
  );
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string | null;
}

export function Textarea({ label, hint, error, className = '', ...rest }: TextareaProps) {
  const auto = useId();
  const id = rest.id ?? auto;
  return (
    <FieldShell label={label} hint={hint} error={error} id={id}>
      <textarea
        id={id}
        aria-invalid={error ? true : undefined}
        className={`field ${error ? 'border-danger' : ''} ${className}`}
        {...rest}
      />
    </FieldShell>
  );
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  error?: string | null;
}

export function Select({ label, hint, error, className = '', children, ...rest }: SelectProps) {
  const auto = useId();
  const id = rest.id ?? auto;
  return (
    <FieldShell label={label} hint={hint} error={error} id={id}>
      <select id={id} className={`field ${className}`} {...rest}>
        {children}
      </select>
    </FieldShell>
  );
}

// ---------------------------------------------------------------------- Badge

const TONE_CLASS = {
  neutral: 'border-ink-800 text-cream-300',
  gold: 'border-gold-600/40 text-gold-200',
  success: 'border-success/40 text-success',
  warning: 'border-warning/40 text-warning',
  danger: 'border-danger/40 text-danger',
  info: 'border-info/40 text-info',
} as const;

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: keyof typeof TONE_CLASS;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${TONE_CLASS[tone]}`}
    >
      {children}
    </span>
  );
}

// --------------------------------------------------------------------- Dialog

/**
 * A focus-trapping, Escape-closing, scroll-locking modal.
 *
 * Portalled to <body> so it can't be clipped by a parent's `overflow: hidden` —
 * the single most common way a hand-rolled modal goes wrong.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      // Focus trap: keep Tab inside the dialog.
      const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables?.length) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    // Focus the panel after paint, so screen readers announce the dialog.
    requestAnimationFrame(() => {
      panelRef.current?.querySelector<HTMLElement>('input, button')?.focus();
    });

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previouslyFocused?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const width = size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-2xl' : 'max-w-lg';

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-ink-950/70 p-4 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`w-full ${width} rounded-xl border border-ink-800 bg-ink-900 shadow-2xl`}
      >
        <div className="border-b border-ink-800 px-6 py-4">
          <h2 id={titleId} className="font-display text-xl text-cream-50">
            {title}
          </h2>
          {description ? (
            <p className="mt-1 text-sm text-cream-300/70">{description}</p>
          ) : null}
        </div>
        {children ? <div className="px-6 py-5">{children}</div> : null}
        {footer ? (
          <div className="flex justify-end gap-3 border-t border-ink-800 px-6 py-4">{footer}</div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

// -------------------------------------------------------------- ConfirmDialog

interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  destructive?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * Replaces `window.confirm()`, which is unstyleable, unthemed, and prefixes the
 * club's own words with "localhost:5173 says…".
 *
 * Usage:  const confirm = useConfirm();
 *         if (await confirm({ title: 'Delete this wine?', destructive: true })) …
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{
    options: ConfirmOptions;
    resolve: (ok: boolean) => void;
  } | null>(null);

  const confirm: ConfirmFn = (options) =>
    new Promise<boolean>((resolve) => setState({ options, resolve }));

  const settle = (ok: boolean) => {
    state?.resolve(ok);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={!!state}
        onClose={() => settle(false)}
        title={state?.options.title ?? ''}
        description={state?.options.description}
        size="sm"
        footer={
          <>
            <Button variant="quiet" onClick={() => settle(false)}>
              Cancel
            </Button>
            <Button
              variant={state?.options.destructive ? 'danger' : 'primary'}
              onClick={() => settle(true)}
            >
              {state?.options.confirmLabel ?? 'Confirm'}
            </Button>
          </>
        }
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}
